/* eslint-disable react-hooks/rules-of-hooks */
import React from "react";
import { Link } from "react-router-dom";
import { LoadingDots, Popover } from "web-ui";

import Footer from "components/Footer";
import styles from "components/dashboard/Dashboard.module.scss";
import DashboardViewport from "components/dashboard/DashboardViewport";
import ReleaseCardHeader from "components/dashboard/release-card/ReleaseCardHeader";
import ReleaseCardTitle, {
  VERSION_OVERRIDES,
  getDisplayVersion,
  hideBuildNumber,
  isNullish,
} from "components/dashboard/release-card/ReleaseCardTitle";
import ReleaseManagersInfo from "components/dashboard/release-managers/ReleaseManagersInfo";
import useErrorScreen from "components/useErrorScreen";
import usePlatformData from "components/usePlatformData";
import { PlatformId } from "util/platform";
import { groupReleasesForCards, selectCardGroups } from "util/release";
import { track } from "util/tracking";

const CI_STATS_LINKS: Partial<Record<PlatformId, string>> = {
  [PlatformId.IOS]: "https://metrics.duolingo.com/dashboards/tK",
  [PlatformId.Android]: "https://metrics.duolingo.com/dashboards/tJ",
};

const CIStatsLink = ({ platformId }: { platformId: string }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const link = CI_STATS_LINKS[platformId.toLowerCase() as PlatformId];

  if (!link) {
    return null;
  }

  return (
    <div style={{ display: "inline-block", position: "relative" }}>
      <a
        href={link}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        rel="noopener noreferrer"
        style={{ fontSize: "16px", marginLeft: "8px" }}
        target="_blank"
      >
        CI Stats
      </a>
      {showTooltip && (
        <Popover direction="down" zIndex={100}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "normal",
              lineHeight: "1.4",
              padding: "0.5em",
              width: "400px",
            }}
          >
            Your Wednesday or Thursday PR must be merged by the time the bot
            runs at 3pm EST. Click here for CI stats to time your PR.
          </div>
        </Popover>
      )}
    </div>
  );
};

// Check if a version is in the values of VERSION_OVERRIDES,
// which means there was a mistake in the card
const isVersionMistake = (majorMinor: string, patch: number): boolean => {
  const targetVersion = `${majorMinor}.${patch}`;
  // Check if any override maps to this version
  return Object.values(VERSION_OVERRIDES).some(
    override => `${override.majorMinor}.${override.patch}` === targetVersion,
  );
};

const Dashboard = () => {
  const { data, error, isLoading, isError } = usePlatformData();
  // State to track which release tiles have tooltips shown
  const [tooltipVisibility, setTooltipVisibility] = React.useState<
    Record<string, boolean>
  >({});
  // State to track which patch is selected on cards that group a minor's patches
  const [selectedPatchByCard, setSelectedPatchByCard] = React.useState<
    Record<string, number>
  >({});

  React.useEffect(() => {
    track("release_dashboard_show_home", {});
  }, []);

  const errorComponent = useErrorScreen(isError, error);

  if (errorComponent) {
    return errorComponent;
  }

  // Define processedData here to avoid conditional hook call
  const processedData = React.useMemo(() => {
    if (!data) {
      return null;
    }

    return data.map(platformData => {
      // First, find any replacement versions in this platform
      const replacementReleases = new Map();

      // Create a map of replacements based on VERSION_OVERRIDES
      Object.entries(VERSION_OVERRIDES).forEach(
        ([originalVersion, replacement]) => {
          // Find the replacement release by majorMinor and patch
          const replacementRelease = platformData.releases.find(
            r =>
              r.majorMinor === replacement.majorMinor &&
              r.patch === replacement.patch,
          );

          if (replacementRelease) {
            replacementReleases.set(originalVersion, replacementRelease);
          }
        },
      );

      // Now, update releases with their replacement data if needed
      const updatedReleases = platformData.releases.map(release => {
        const versionKey = `${release.majorMinor}.${release.patch}`;

        // If this version needs to be updated with replacement data
        if (VERSION_OVERRIDES[versionKey]) {
          const replacementRelease = platformData.releases.find(
            r =>
              r.majorMinor === VERSION_OVERRIDES[versionKey].majorMinor &&
              r.patch === VERSION_OVERRIDES[versionKey].patch,
          );

          // If we found the replacement release, use its metrics data
          if (replacementRelease?.readiness) {
            return {
              ...release,
              readiness: {
                ...replacementRelease.readiness,
              },
              // We don't need to override majorMinor or patch here since ReleaseCardTitle
              // will handle the display transformation using VERSION_OVERRIDES
            };
          }
        }

        return release;
      });

      return {
        ...platformData,
        releases: updatedReleases,
      };
    });
  }, [data]);

  if (!processedData && isLoading) {
    return <LoadingDots type="screen-white" />;
  }

  if (!processedData) {
    return null;
  }

  return (
    <DashboardViewport>
      {processedData.map((platformData, i) => (
        <React.Fragment key={i}>
          <div className={styles.header}>
            <ReleaseManagersInfo
              key={`platform-${i}`}
              platformId={platformData.platform.id}
            />
            <div className={styles.name}>
              <Link
                title={`View release history for ${platformData.platform.name}`}
                to={`/history/${platformData.platform.id.toLowerCase()}`}
              >
                {platformData.platform.name}
              </Link>
              <CIStatsLink platformId={platformData.platform.id} />
            </div>
          </div>
          {selectCardGroups(groupReleasesForCards(platformData.releases)).map(
            (releaseGroup, j) => {
              const cardKey = `${platformData.platform.id}-${releaseGroup[0].majorMinor}`;
              const release =
                releaseGroup.find(
                  r => r.patch === selectedPatchByCard[cardKey],
                ) ?? releaseGroup[0];
              const isMistake = isVersionMistake(
                release.majorMinor,
                release.patch,
              );
              const releaseKey = `${platformData.platform.id}-${release.majorMinor}-${release.patch}`;
              // Get the version information to use in the changelog link
              const versionKey = `${release.majorMinor}.${release.patch}`;
              const displayMajorMinor = VERSION_OVERRIDES[versionKey]
                ? VERSION_OVERRIDES[versionKey].majorMinor
                : release.majorMinor;

              const changelog = (
                <Link
                  className={styles["open-changelog"]}
                  title="View changelog"
                  to={`/changelog/${encodeURIComponent(
                    platformData.platform.id.toLowerCase(),
                  )}?fromVersion=${encodeURIComponent(displayMajorMinor)}`}
                >
                  <ReleaseCardTitle
                    minorOnly={releaseGroup.length > 1}
                    platformData={platformData}
                    release={release}
                  />
                </Link>
              );

              const stats = (
                <ul className={styles.stats}>
                  {release.readiness?.metrics
                    .filter(metric => metric.shouldShowInSummary)
                    .map((readinessItem, k) =>
                      readinessItem ? (
                        <li className={styles.statsItem} key={k}>
                          <a
                            href={readinessItem.url}
                            rel="noopener noreferrer"
                            target="_blank"
                            title={
                              readinessItem.timestamp
                                ? `Data as of ${new Date(readinessItem.timestamp * 1000).toLocaleString()}`
                                : undefined
                            }
                          >
                            {readinessItem.label}:{" "}
                            <strong>{readinessItem.formattedValue}</strong>
                          </a>
                        </li>
                      ) : null,
                    )}
                  {platformData.openPatchCount > 0 && j === 0 && (
                    <li className={styles.statsItem}>
                      <a
                        href={
                          `https://github.com/duolingo/${platformData.platform.repo}/pulls` +
                          "?q=is%3Aopen+is%3Apr+label%3Apatch" +
                          `${
                            platformData.platform.id.toLowerCase() ===
                            PlatformId.China
                              ? "+chin"
                              : ""
                          }`
                        }
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Open patch PRs:
                        <strong> {platformData.openPatchCount} </strong>
                      </a>
                    </li>
                  )}
                </ul>
              );

              return (
                <div
                  className={styles.release}
                  key={cardKey}
                  onMouseEnter={() => {
                    if (isMistake) {
                      setTooltipVisibility({
                        ...tooltipVisibility,
                        [releaseKey]: true,
                      });
                    }
                  }}
                  onMouseLeave={() => {
                    if (isMistake) {
                      setTooltipVisibility({
                        ...tooltipVisibility,
                        [releaseKey]: false,
                      });
                    }
                  }}
                  style={{
                    background: release.color,
                    position: "relative",
                  }}
                >
                  {isMistake && tooltipVisibility[releaseKey] && (
                    <div
                      style={{
                        background: "rgba(0, 0, 0, 0.8)",
                        borderRadius: "4px",
                        color: "white",
                        fontSize: "14px",
                        maxWidth: "250px",
                        padding: "8px 12px",
                        pointerEvents: "none",
                        position: "absolute",
                        right: "8px",
                        top: "8px",
                        zIndex: 100,
                      }}
                    >
                      There was likely an inconsistency with AppStore and plist
                      version. Check #topic-ios-learning-app
                    </div>
                  )}
                  <ReleaseCardHeader
                    platformData={platformData}
                    release={release}
                  />
                  <div className={styles.releaseCardBody}>
                    {changelog}
                    {stats}
                  </div>
                  {releaseGroup.length > 1 && (
                    <div className={styles.patchTabs}>
                      {releaseGroup.map(tabRelease => (
                        <button
                          className={
                            tabRelease.patch === release.patch
                              ? `${styles.patchTab} ${styles.patchTabSelected}`
                              : styles.patchTab
                          }
                          key={tabRelease.patch}
                          onClick={() =>
                            setSelectedPatchByCard({
                              ...selectedPatchByCard,
                              [cardKey]: tabRelease.patch,
                            })
                          }
                          title={`${tabRelease.majorMinor}.${tabRelease.patch}: ${tabRelease.status}`}
                          type="button"
                        >
                          <span
                            className={styles.patchTabStatus}
                            style={{ background: tabRelease.color }}
                          />
                          .
                          {
                            getDisplayVersion(
                              tabRelease.majorMinor,
                              tabRelease.patch,
                            ).patch
                          }
                          {isNullish(tabRelease.build) ||
                          hideBuildNumber(platformData)
                            ? ""
                            : `.${tabRelease.build}`}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            },
          )}
        </React.Fragment>
      ))}
      <div className={styles.footerOuter}>
        <Footer className={styles.footer} />
      </div>
    </DashboardViewport>
  );
};

export default Dashboard;
